import 'construct-style-sheets-polyfill';

import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { autorun, reaction } from 'mobx';
import { store } from '@stores';
import { RoutesEnum } from '@constants/sidebarLinks';

import { googleTags } from './googleTags';
import { sentryInit } from './sentry';
import '@components/app/app-notificator';
import { router } from './router';
import { AuthStatusEnum } from './stores/types';

import('@components/app/app-loader');

sentryInit();

const EXISTED_LINKS = [
  '/payment',
  '/',
  '/login',
  '/2fa',
  '/payments',
  '/transactions-history',
  '/balance',
  '/api',
  '/settings',
  '/reports',
  String(RoutesEnum.paymentsTools),
  String(RoutesEnum.subUsers),
];

@customElement('hero-payments')
export default class HeroPayments extends LitElement {
  @state()
  private status;

  @state()
  private loading = true;

  constructor() {
    super();

    autorun(() => {
      this.status = store.sessionStore.status;
      this.loading = !store.appHasBeenInitiated;
    });

    // TODO: fixme please via centralized permission center
    reaction(
      () => store.routesStore.url,
      (url) => {
        if (url === '/404') {
          this.status = '404';
          return;
        }
        if (this.loading) {
          /*
            case when we haven't checked auth because user got 404
            at the very beginning and tries to go to homepage
          */
          store.sessionStore.checkAuth();
        } else {
          this.status = store.sessionStore.status;
        }
      },
    );

    Object.defineProperty(window, '__APP_VERSION__', { value: process.env.npm_package_version });

    router((location) => this.#routing(location));
    this.#insertGoogleScript();
    this.#insertRecaptchaScript();
  }

  #routing(location) {
    const { pathname } = location;
    const link = EXISTED_LINKS.includes(pathname);

    if (!pathname || !link) {
      this.status = '404';

      return;
    }

    if (pathname === '/2fa' && !store.sessionStore.hasLCredentialsAfterLocalAuthStrategy) {
      store.redirect('/login');
    }

    if (store.sessionStore.status === AuthStatusEnum.twoFaRequired) {
      return;
    }

    store.sessionStore.checkAuth();
  }

  #insertGoogleScript() {
    if (process.env.NODE_ENV !== 'production') return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.text = googleTags.script;
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  #insertRecaptchaScript() {
    // if (process.env.NODE_ENV !== 'production') return;

    const style = document.createElement('style');
    style.innerText = '.grecaptcha-badge { visibility: hidden; }';
    document.getElementsByTagName('head')[0].appendChild(style);

    const script = document.createElement('script');
    script.type = 'text/javascript';
    // eslint-disable-next-line no-underscore-dangle
    script.src = `https://www.google.com/recaptcha/api.js?render=${(<any>window)._env_.RECAPTCHA_SITE_KEY}`;
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  render() {
    if (this.loading && this.status !== '404') {
      return html`<app-loader />`;
    }

    switch (this.status) {
      case AuthStatusEnum.payment:
        import('@pages/page-payment');
        return html`<page-payment></page-payment>`;

      case AuthStatusEnum.logged:
        import('@pages/page-dashboard');
        return html`<page-dashboard></page-dashboard>`;

      case AuthStatusEnum.unchecked:
        return null;

      case '404':
        import('@pages/page-404');
        return html`<page-404></page-404>`;

      case AuthStatusEnum.anon:
      case AuthStatusEnum.twoFaRequired:
      default:
        import('@pages/page-auth');
        return html`<page-auth></page-auth>`;
    }
  }
}
